import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService, passwordRules } from '@app/core';
import { environment } from '@app/environment';

/**
 * Change of the own password (user menu, 2023 pen test finding 10). Cognito
 * ChangePassword needs the current password; the new one has to meet the
 * rules of the user pool, which the form shows while typing. Cognito's
 * answer to a failed change is shown in the form.
 */
@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChangePasswordComponent {
  private changeDetector = inject(ChangeDetectorRef);
  private auth = inject(AuthService);
  private router = inject(Router);
  private location = inject(Location);

  public readonly logo = environment.branding.logo.onLight;
  public readonly appName = environment.branding.appName;
  public readonly rules = passwordRules;

  public oldPassword = '';
  public newPassword = '';
  public verifyPassword = '';
  /** Set by the first submit: from then on the fields show their errors. */
  public submitted = false;
  public busy = false;
  /** Cognito's answer to the last attempt, if it failed. */
  public error: string | null = null;

  get rulesMet(): boolean {
    return this.rules.every((rule) => rule.test(this.newPassword));
  }

  get passwordsMatch(): boolean {
    return this.newPassword === this.verifyPassword;
  }

  get differsFromOld(): boolean {
    return !this.newPassword || this.newPassword !== this.oldPassword;
  }

  get oldPasswordMissing(): boolean {
    return this.submitted && !this.oldPassword;
  }

  get newPasswordInvalid(): boolean {
    return this.submitted && (!this.rulesMet || !this.differsFromOld);
  }

  get verifyInvalid(): boolean {
    return this.submitted && (!this.verifyPassword || !this.passwordsMatch);
  }

  get valid(): boolean {
    return (
      !!this.oldPassword &&
      this.rulesMet &&
      this.passwordsMatch &&
      this.differsFromOld
    );
  }

  changePassword(event: Event) {
    event.preventDefault();
    this.submitted = true;
    this.error = null;
    if (!this.valid || this.busy) {
      return;
    }
    this.busy = true;
    this.auth
      .changePassword(this.oldPassword, this.newPassword)
      .subscribe((result) => {
        this.busy = false;
        if (result.ok) {
          this.router.navigateByUrl('/home');
        } else {
          this.error = result.message;
        }
        this.changeDetector.markForCheck();
      });
  }

  cancel() {
    // Back to the page the user came from, else home.
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigateByUrl('/home');
    }
  }
}
